import { cancelOnSessionChange } from '../session-cancellation';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NGXLogger } from 'ngx-logger';
import { mergeMap, map, catchError, tap, takeUntil, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { OrganizationsService } from '@api/services/organizations.service';
import * as OrganizationsActions from '../../actions/organizations/users.actions';
import { MessageService } from 'primeng/api';
import { ApiDefOrganizationData } from '@api/models/api-def-organization-data';
import { ApiDefApiKeyData } from '@api/models/api-def-api-key-data';

@Injectable()
export class OrganizationsUsersEffects {
  loadOrganizationUsers$;
  updateOrganizationUserRole$;
  removeOrganizationUser$;
  updateOrganizationUserRoleSuccess$;
  updateOrganizationUserRoleFailure$;
  removeOrganizationUserSuccess$;
  removeOrganizationUserFailure$;

  constructor(
    private logger: NGXLogger,
    private actions$: Actions,
    private organizationsService: OrganizationsService,
    private messageService: MessageService
  ) {


    // Organization Users & Invitations Effects
    this.loadOrganizationUsers$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.loadOrganizationUsers),
        mergeMap(({ organizationId }) =>
          this.organizationsService.organizationsGetUsers({ organizationId }).pipe(
            map((users) =>
              OrganizationsActions.loadOrganizationUsersSuccess({
                organizationId,
                users: users.filter(u => u.user && u.role).map(u => ({ user: u.user!, role: u.role! }))
              })
            ),
            catchError((error) => of(OrganizationsActions.loadOrganizationUsersFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.updateOrganizationUserRole$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.updateOrganizationUserRole),
        mergeMap(({ organizationId, userId, role }) =>
          this.organizationsService.organizationsUpdateUserRole({ organizationId, userId, body: { role } }).pipe(
            map((result) =>
              OrganizationsActions.updateOrganizationUserRoleSuccess({
                organizationId,
                user: result.user!,
                role: result.role!,
                organization: { data: result.organization!, loadedAt: new Date() }
              })
            ),
            catchError((error) => of(OrganizationsActions.updateOrganizationUserRoleFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );

    this.removeOrganizationUser$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.removeOrganizationUser),
        mergeMap(({ organizationId, userId }) =>
          this.organizationsService.organizationsRemoveUser({ organizationId, userId }).pipe(
            map((result) =>
              OrganizationsActions.removeOrganizationUserSuccess({
                organizationId,
                userId,
                organization: result?.organization
                  ? { data: result.organization, loadedAt: new Date() }
                  : undefined
              })
            ),
            catchError((error) => of(OrganizationsActions.removeOrganizationUserFailure({ organizationId, error }))),
            cancelOnSessionChange(this.actions$)
          )
        )
      )
    );


    // Show success message when user role is updated
    this.updateOrganizationUserRoleSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.updateOrganizationUserRoleSuccess),
        tap(({ user, role }) => {
          this.messageService.add({ severity: 'success', summary: 'Role Updated', detail: `Role updated to ${role} for ${user?.email || 'user'}.` });
        })
      ),
      { dispatch: false }
    );

    // Show error message when updating user role fails
    this.updateOrganizationUserRoleFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.updateOrganizationUserRoleFailure),
        tap(({ error }) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update user role.' });
          this.logger.error('Update organization user role failed', error);
        })
      ),
      { dispatch: false }
    );

    // Show success message when user is removed
    this.removeOrganizationUserSuccess$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.removeOrganizationUserSuccess),
        tap(() => {
          this.messageService.add({ severity: 'success', summary: 'User Removed', detail: 'User removed from organization.' });
        })
      ),
      { dispatch: false }
    );

    // Show error message when removing user fails
    this.removeOrganizationUserFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(OrganizationsActions.removeOrganizationUserFailure),
        tap(({ error }) => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to remove user.' });
          this.logger.error('Remove organization user failed', error);
        })
      ),
      { dispatch: false }
    );

  }

}